import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rewardApi } from '../../../api/reward.api';
import { AdminRewardHistoryItem } from '../../../types/Reward.api.type';
import { UserDetail } from '../UserDetail';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { AccessDots } from '../AccessDots';

/** 리워드 종류 한국어 매핑 */
const REWARD_TYPE_LABELS: Record<string, string> = {
  VOTE: '투표',
  VOTE_AD_BONUS: '투표광고보너스',
  DAILY_BONUS: '일일 보너스',
  DAILY_AD_BONUS: '오늘의투표광고보너스',
  WEEKLY_MILESTONE_1: '주간 1단계',
  WEEKLY_MILESTONE_2: '주간 2단계',
  WEEKLY_MILESTONE_3: '주간 3단계',
  CREATOR_MILESTONE: '설문 작성 보너스',
  FIRST_VOTE_BONUS: '첫 투표 보너스',
  MILESTONE_AD_BONUS: '누적투표보너스',
  ATTENDANCE: '출석',
  ATTENDANCE_AD_BONUS: '출석광고보너스',
  ATTENDANCE_STREAK: '연속출석보너스',
  ATTENDANCE_STREAK_SAVE: '연속출석유지보너스',
  COMMENT: '댓글 보상',
  COMMENT_AD_BONUS: '댓글 광고보너스',
  SURVEY_LIKE: '설문 좋아요 보상',
  SURVEY_LIKE_AD_BONUS: '설문 좋아요 광고보너스',
  COMMENT_LIKE: '댓글 좋아요 보상',
  COMMENT_LIKE_AD_BONUS: '댓글 좋아요 광고보너스',
  WEEKLY_MISSION: '이번주 미션 완료',
  WEEKLY_MISSION_AD_BONUS: '이번주 미션 광고보너스',
};

/** 통화 뱃지 색상 */
const CURRENCY_COLORS: Record<string, string> = {
  PIECE: 'bg-amber-100 text-amber-700',
  TOSS_POINT: 'bg-blue-100 text-blue-700',
};
const CURRENCY_LABELS: Record<string, string> = {
  PIECE: '조각',
  TOSS_POINT: '토스포인트',
};

/** 상태 색상 매핑 */
const STATUS_COLORS: Record<string, string> = {
  SUCCESS: 'bg-green-100 text-green-700',
  PENDING: 'bg-yellow-100 text-yellow-700',
  FAILED: 'bg-red-100 text-red-700',
};

/** 상태 한국어 매핑 */
const STATUS_LABELS: Record<string, string> = {
  SUCCESS: '성공',
  PENDING: '대기',
  FAILED: '실패',
};

export const RewardHistoryPanel = () => {
  const [page, setPage] = useState(0);
  const [userIdInput, setUserIdInput] = useState('');
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const pageSize = 20;

  const handleSearch = () => {
    const parsed = userIdInput.trim() ? Number(userIdInput.trim()) : undefined;
    setUserId(parsed && !isNaN(parsed) ? parsed : undefined);
    setPage(0);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['adminRewardHistory', page, userId],
    queryFn: () => rewardApi.getHistory(page, pageSize, userId).then((res) => res.data.results),
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('ko-KR') + '원';
  };

  if (selectedUserId !== null) {
    return <UserDetail userId={selectedUserId} onBack={() => setSelectedUserId(null)} />;
  }

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700">리워드 적립 내역</h3>
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder="유저 ID"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-28 px-2 py-1 text-sm border rounded"
            />
            <Button variant="outline" size="sm" onClick={handleSearch}>
              <Search className="w-3 h-3" />
            </Button>
            {userId != null && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setUserIdInput('');
                  setUserId(undefined);
                  setPage(0);
                }}
              >
                초기화
              </Button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-gray-400">로딩 중...</div>
        ) : !data?.content?.length ? (
          <div className="text-center py-8 text-gray-400">지급 내역이 없습니다.</div>
        ) : (
          <>
            {/* 데스크톱 테이블 */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-2 pr-3">ID</th>
                    <th className="pb-2 pr-3">유저</th>
                    <th className="pb-2 pr-3">종류</th>
                    <th className="pb-2 pr-3">통화</th>
                    <th className="pb-2 pr-3">금액</th>
                    <th className="pb-2 pr-3">상태</th>
                    <th className="pb-2 pr-3">에러</th>
                    <th className="pb-2 pr-3">설문ID</th>
                    <th className="pb-2">날짜</th>
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((item: AdminRewardHistoryItem) => (
                    <tr
                      key={item.id}
                      className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedUserId(item.userId)}
                    >
                      <td className="py-2 pr-3 text-gray-400">{item.id}</td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-col gap-1">
                          <span>{item.nickname}</span>
                          <AccessDots access={item.recentAccess} />
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className="text-xs">
                          {REWARD_TYPE_LABELS[item.rewardType] ?? item.rewardType}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3">
                        <span
                          className={`px-1.5 py-0.5 rounded text-xs ${CURRENCY_COLORS[item.rewardCurrency ?? 'PIECE'] ?? 'bg-gray-100 text-gray-500'}`}
                        >
                          {CURRENCY_LABELS[item.rewardCurrency ?? 'PIECE'] ?? item.rewardCurrency}
                        </span>
                      </td>
                      <td className="py-2 pr-3 font-medium">{formatAmount(item.amount)}</td>
                      <td className="py-2 pr-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[item.status] ?? 'bg-gray-100 text-gray-500'}`}
                        >
                          {STATUS_LABELS[item.status] ?? item.status}
                        </span>
                      </td>
                      <td
                        className="py-2 pr-3 text-xs text-red-500 max-w-[200px] truncate"
                        title={item.errorMessage ?? undefined}
                      >
                        {item.status === 'FAILED' && item.errorMessage ? item.errorMessage : '-'}
                      </td>
                      <td className="py-2 pr-3 text-gray-400">{item.questionId ?? '-'}</td>
                      <td className="py-2 text-gray-400 text-xs">{formatDate(item.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 모바일 카드 */}
            <div className="md:hidden space-y-3">
              {data.content.map((item: AdminRewardHistoryItem) => (
                <div
                  key={item.id}
                  className="border rounded-lg p-3 space-y-2 cursor-pointer hover:bg-gray-50"
                  onClick={() => setSelectedUserId(item.userId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.nickname}</span>
                      <AccessDots access={item.recentAccess} />
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[item.status] ?? 'bg-gray-100 text-gray-500'}`}
                    >
                      {STATUS_LABELS[item.status] ?? item.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1">
                      <Badge variant="outline" className="text-xs">
                        {REWARD_TYPE_LABELS[item.rewardType] ?? item.rewardType}
                      </Badge>
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs ${CURRENCY_COLORS[item.rewardCurrency ?? 'PIECE'] ?? 'bg-gray-100 text-gray-500'}`}
                      >
                        {CURRENCY_LABELS[item.rewardCurrency ?? 'PIECE'] ?? item.rewardCurrency}
                      </span>
                    </div>
                    <span className="font-bold">{formatAmount(item.amount)}</span>
                  </div>
                  {item.status === 'FAILED' && item.errorMessage && (
                    <div className="text-xs text-red-500">{item.errorMessage}</div>
                  )}
                  <div className="text-xs text-gray-400">{formatDate(item.createdAt)}</div>
                </div>
              ))}
            </div>

            {/* 페이지네이션 */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t">
              <span className="text-xs text-gray-500">총 {data.totalElements}건</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-600">
                  {page + 1} / {data.totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= (data.totalPages || 1) - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};
